import React from 'react'
import Typography from '@material-ui/core/Typography'
import { Box, Grid, makeStyles, useMediaQuery, useTheme } from '@material-ui/core'
import { ResearcherDetailViewType, ResearcherDetailCtrlType } from './researcherDetailType'
import useResearchDetail from './useResearcherDetail'
import MyTextfield from '../../../../control/textfield/textfield'
import useTranslate from '../../../../i18n/useTranslate'
import MyAccordion from '../../../../control/accordion/accordion'
import { ResCoResearcher } from '../../project/flow/requestNewProject/researcher/researcherOtherType'

const ResearcherDetailView: React.FC<ResearcherDetailViewType> = ({ ...props }) => {
    const { initialData, hideAccordion = false } = props
    const translate = useTranslate()
    const theme = useTheme()
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'))
    const isIPadView = useMediaQuery(theme.breakpoints.down('md'))
    const classes = useStyles({ isMobileView, isIPadView })

    const getResearcherName = (data: ResCoResearcher, lang: 'th' | 'en') => {
        const isTh = lang === 'th'

        const titleValue = data.titleName?.value?.toString()
        const isOtherTitle = titleValue === '5'
        const isNoTitle = titleValue === '4'

        let titleName: string | undefined

        if (isNoTitle) {
            titleName = undefined
        } else if (isOtherTitle) {
            titleName = isTh ? data.otherTitleNameTh : data.otherTitleNameEn
        } else {
            titleName = isTh ? data.titleName?.nameTH : data.titleName?.nameEN
        }

        const name = isTh ? data.nameTh : data.nameEn

        return [titleName, name].filter(Boolean).join(' ')
    }

    const FieldRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
        return (
            <Box className={classes.row}>
                <Typography variant="body2" className={classes.label}>
                    {label} :
                </Typography>
                <Typography variant="body2" className={classes.value}>
                    {value ?? '-'}
                </Typography>
            </Box>
        )
    }

    const renderElement = () => (
        <Grid direction="column" spacing={4}>
            <Box mb={4}>
                {initialData?.coResearcher?.map((r, idx) => (
                    <Grid key={idx} container spacing={2} xs={12}>
                        <Grid container item>
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                {idx === 0 ?
                                translate.get('LABEL.RESEARCHER_MAIN', [String(idx + 1)])
                                : translate.get('LABEL.RESEARCHER_N', [String(idx + 1)]) 
                                }
                            </Typography>
                        </Grid>

                        <Grid container item>
                            <FieldRow
                                label={translate.get('LABEL.RESEARCHER_TYPE')}
                                value={
                                    translate.languageStore.currentLanguage === translate.languageKey.th
                                        ? r?.researcherType?.nameTH
                                        : r?.researcherType?.nameEN
                                }
                            />
                        </Grid>

                        <Grid container item>
                            <FieldRow
                                label={translate.get('LABEL.EDUCATION_LEVEL')}
                                value={
                                    translate.languageStore.currentLanguage === translate.languageKey.th
                                        ? r?.educationLevel?.nameTH || '-'
                                        : r?.educationLevel?.nameEN || '-'
                                }
                            />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={12}>
                            <FieldRow
                                label={translate.get('LABEL.ACADEMIC_TITLE')}
                                value={
                                    translate.languageStore.currentLanguage === translate.languageKey.th
                                        ? r?.academicPosition?.nameTH || r?.otherAcademicPositionTh || '-'
                                        : r?.academicPosition?.nameEN || r?.otherAcademicPositionEn || '-'
                                }
                            />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={6}>
                            <FieldRow
                                label={`${translate.get('LABEL.FL_NAME')}​ (${translate.get('LABEL.THAI')}​)`}
                                value={getResearcherName(r, 'th')}
                            />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={6}>
                            <FieldRow
                                label={`${translate.get('LABEL.FL_NAME')}​ (${translate.get('LABEL.ENG')}​)`}
                                value={getResearcherName(r, 'en')}
                            />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={6}>
                            <FieldRow label={translate.get('LABEL.TELEPHONE_NUMBER')} value={r?.homePhoneNumber} />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={6}>
                            <FieldRow label={translate.get('LABEL.FAX')} value={r?.fax} />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={6}>
                            <FieldRow label={translate.get('LABEL.MOBILE_PHONE_NO')} value={r?.phoneNumber} />
                        </Grid>

                        <Grid container item xs={12} md={12} lg={6}>
                            <FieldRow label={translate.get('LABEL.EMAIL')} value={r?.email} />
                        </Grid>

                        {idx < initialData.coResearcher.length - 1 && (
                            <Grid item xs={12} md={12} lg={12}>
                                <Typography className={classes.line} />
                            </Grid>
                        )}
                    </Grid>
                ))}
            </Box>
        </Grid>
    )

    return (
        <>
            {hideAccordion ? (
                renderElement()
            ) : (
                <MyAccordion title={translate.get('LABEL.RESEARCHER_DETAIL')}>{renderElement()}</MyAccordion>
            )}
        </>
    )
}

let ResearcherDetailCtrl: React.FC<ResearcherDetailCtrlType> = ({ ...props }) => {
    const researchDetailModel = useResearchDetail({ ...props })
    return <ResearcherDetailView {...researchDetailModel} {...props} />
}

export default ResearcherDetailCtrl

const useStyles = makeStyles(() => ({
    typography: {
        marginLeft: (props: any) => {
            let value = '0px'
            if (props.isMobileView || props.isIPadView) {
                value = '0px'
            } else {
                value = '10px'
            }
            return value
        },
    },
    line: {
        borderTop: '1px solid #c7c7c7',
        marginTop: '12px',
        marginBottom: '16px',
    },
    row: (props: any) => ({
        display: 'flex',
        flexDirection: props.isMobileView ? 'column' : 'row',
        alignItems: props.isMobileView ? 'flex-start' : 'baseline',
        gap: props.isMobileView ? 4 : 6,
        marginBottom: 8,
        flexWrap: props.isMobileView ? 'nowrap' : 'wrap',
    }),
    label: { whiteSpace: 'nowrap' },
    value: { flex: 1, minWidth: 0, wordBreak: 'break-word', display: 'contents' },
}))
